import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar, MapPin, Ticket, X } from "lucide-react";
import api, { formatARS, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSettings from "@/hooks/useSettings";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { setSeo } from "@/lib/seo";

const TYPE_LABEL = { desfile: "Desfile", lanzamiento: "Lanzamiento", exposicion: "Exposición", presentacion: "Presentación" };

export default function Events() {
  const settings = useSettings();
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setSeo({ title: "Eventos", description: "Desfiles, lanzamientos y presentaciones de ARCHIVE LAB. Anotate o comprá tu entrada.", path: "/eventos" });
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  if (settings.events_enabled === false) {
    return (
      <div className="px-4 sm:px-8 lg:px-16 py-24 lg:py-32 text-center" data-testid="events-disabled">
        <div className="dossier-label text-[#565A61] mb-4">Agenda del archivo</div>
        <h1 className="font-display uppercase tracking-tight text-4xl lg:text-5xl text-[#171719]">Próximamente</h1>
        <p className="text-[#565A61] mt-4 max-w-md mx-auto">No hay eventos publicados por ahora. En cuando haya uno, lo vas a ver acá primero.</p>
        <Link to="/" className="inline-block mt-8 btn-outline-ink px-6 py-3 dossier-label">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-8 lg:px-16 py-12 lg:py-16">
      <div className="mb-10">
        <div className="dossier-label text-[#565A61] mb-3">Agenda del archivo</div>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl lg:text-6xl text-[#171719]">Eventos</h1>
        <p className="text-[#565A61] mt-3 max-w-xl">Desfiles, lanzamientos y presentaciones. Anotate a los de entrada libre o comprá tu entrada con el mismo pago que las piezas.</p>
      </div>

      {events.length === 0 ? (
        <p className="py-20 text-center dossier-label text-[#565A61]">Todavía no hay eventos publicados.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {events.map((ev, i) => (
            <motion.div key={ev.id} data-testid={`event-card-${ev.id}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className="museum-frame overflow-hidden hover-lift flex flex-col">
              <div className="img-zoom aspect-[16/10] bg-[#CFD1D6] overflow-hidden relative">
                {ev.image && <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3 dossier-label bg-black/70 text-[#E8E9EB] px-2 py-1 border border-[#2A2A2A]">{TYPE_LABEL[ev.type] || ev.type}</div>
                {ev.sold_out && <div className="absolute top-3 right-3 dossier-label bg-[#2A2A2A] text-[#E8E9EB] px-2 py-1">sin cupos</div>}
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-display font-bold uppercase tracking-tight text-xl text-[#171719]">{ev.title}</h3>
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#565A61]">
                  <span className="inline-flex items-center gap-1.5"><Calendar size={13} /> {ev.date || "A confirmar"}</span>
                  <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {ev.location || "A confirmar"}</span>
                </div>
                {ev.description && <p className="text-sm text-[#565A61] mt-3 leading-relaxed flex-1">{ev.description}</p>}
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-[#C9CBD1]">
                  <span className="text-sm text-[#171719]">{ev.price != null ? formatARS(ev.price) : "Entrada libre"}</span>
                  {ev.spots_left != null && <span className="dossier-label text-[#565A61]">{ev.spots_left} cupos</span>}
                </div>
                <button data-testid={`event-register-${ev.id}`} disabled={ev.sold_out} onClick={() => setActive(ev)}
                  className={`mt-4 w-full py-3 dossier-label inline-flex items-center justify-center gap-2 ${ev.sold_out ? "bg-[#CFD1D6] text-[#565A61] cursor-not-allowed" : "btn-ink"}`}>
                  <Ticket size={15} /> {ev.sold_out ? "Sin cupos" : ev.price != null ? "Comprar entrada" : "Anotarme"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {active && <RegisterModal ev={active} onClose={() => setActive(null)} onDone={() => { setActive(null); api.get("/events").then((r) => setEvents(r.data)); }} />}
    </div>
  );
}

function RegisterModal({ ev, onClose, onDone }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const paid = ev.price != null;

  const submit = async () => {
    if (!name || !email) return toast.error("Completá nombre y email");
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${ev.id}/register`, { name, email });
      if (data.paid) {
        if (data.demo) navigate(`/checkout/demo/${data.order_id}`);
        else if (data.checkout_url) window.location.href = data.checkout_url;
      } else {
        toast.success("¡Listo! Quedaste anotada", { description: ev.title });
        onDone();
      }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#F4F4F3] border border-[#C4C7CD] w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="event-register-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#C9CBD1]">
          <h2 className="font-display font-bold uppercase tracking-tight text-lg text-[#171719]">{paid ? "Comprar entrada" : "Anotarme"}</h2>
          <button onClick={onClose} className="text-[#565A61] hover:text-[#171719]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="dossier-label text-[#565A61]">{ev.title} · {paid ? formatARS(ev.price) : "entrada libre"}</div>
          <div><Label className="dossier-label text-[#565A61]">Nombre y apellido</Label><Input data-testid="event-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-[#F4F4F3] border-[#C4C7CD] text-[#171719]" /></div>
          <div><Label className="dossier-label text-[#565A61]">Email</Label><Input data-testid="event-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-[#F4F4F3] border-[#C4C7CD] text-[#171719]" /></div>
          <button data-testid="event-confirm" onClick={submit} disabled={busy} className="btn-ink w-full py-3.5 dossier-label disabled:opacity-60">
            {busy ? "Procesando…" : paid ? "Pagar entrada con Mercado Pago" : "Confirmar inscripción"}
          </button>
        </div>
      </div>
    </div>
  );
}
